import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { ClipboardList, CreditCard, CheckCircle2, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { payForBooking } from "@/lib/razorpay";
import { waLink, bookingChatText } from "@/lib/whatsapp";

const STATUS_COLORS = {
  Pending: "bg-linen text-charcoal border-warm",
  Confirmed: "bg-terracotta/10 text-terracotta border-terracotta/40",
  "In Progress": "bg-sage/10 sage border-sage/40",
  Completed: "bg-sage text-white border-sage",
  Cancelled: "bg-linen text-charcoal/50 border-warm line-through",
};

const payableOf = (b) => (b.confirmation_flow === "whatsapp" ? (b.customer_approved && b.final_amount > 0 ? b.final_amount : null) : b.total || null);
const needsPayment = (b) => !!payableOf(b) && ["Pending", "Failed"].includes(b.payment_status) && !["Cancelled", "Completed"].includes(b.status);

const AwaitingBlock = ({ b, i, whatsapp }) => (
  <div className="mt-2 flex flex-col items-end gap-2" data-testid={`booking-awaiting-${i}`}>
    <span className="text-xs font-semibold text-violet-600">
      {b.final_amount ? `Quoted ₹${b.final_amount} · awaiting your approval on WhatsApp` : "Awaiting confirmation on WhatsApp"}
    </span>
    {whatsapp && (
      <a href={waLink(whatsapp, bookingChatText(b.id))} target="_blank" rel="noreferrer" data-testid={`booking-whatsapp-${i}`}
        className="pill-btn pill-btn-secondary !py-2 !px-4 text-sm flex items-center gap-2">
        <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
      </a>
    )}
  </div>
);

const PaymentBlock = ({ b, i, onPaid, whatsapp }) => {
  const [busy, setBusy] = useState(false);
  if (b.payment_status === "Paid") {
    return <p className="text-xs font-semibold text-emerald-600 mt-2 flex items-center justify-end gap-1" data-testid={`booking-paid-${i}`}><CheckCircle2 className="w-3.5 h-3.5" /> Paid</p>;
  }
  if (b.confirmation_flow === "whatsapp" && !payableOf(b) && b.status !== "Cancelled") return <AwaitingBlock b={b} i={i} whatsapp={whatsapp} />;
  if (!needsPayment(b)) return null;
  const pay = async () => {
    setBusy(true);
    try {
      const r = await payForBooking(b.id);
      if (r === "paid") { toast.success("Payment received. Your booking is confirmed!"); onPaid(); }
      else if (r === "failed") { toast.error("Payment failed. Please try again."); onPaid(); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not start payment");
      onPaid();
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex flex-col items-end gap-2">
      <span className={`text-xs font-semibold ${b.payment_status === "Failed" ? "text-rose-600" : "text-amber-600"}`} data-testid={`booking-payment-status-${i}`}>
        {b.payment_status === "Failed" ? "Payment failed" : b.confirmation_flow === "whatsapp" ? "Final price approved · payment pending" : "Payment pending"}
      </span>
      <button onClick={pay} disabled={busy} data-testid={`booking-pay-now-${i}`}
        className="pill-btn pill-btn-primary !py-2 !px-4 text-sm flex items-center gap-2">
        <CreditCard className="w-4 h-4" /> {busy ? "Opening..." : `Pay ₹${payableOf(b)} now`}
      </button>
    </div>
  );
};

export default function Bookings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [whatsapp, setWhatsapp] = useState("");
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/bookings").then((r) => setItems(r.data)).finally(() => setLoading(false)), []);
  useEffect(() => {
    load();
    api.get("/settings/public").then((r) => setWhatsapp(r.data.business_whatsapp || "")).catch(() => {});
  }, [load]);

  return (
    <div className="space-y-8" data-testid="bookings-page">
      <div>
        <h1 className="font-display text-4xl text-charcoal">Your bookings</h1>
        <p className="text-charcoal/60 mt-2">Every celebration you've arranged with us.</p>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(2)].map((_, i) => <div key={i} className="card-surface h-32 animate-pulse bg-linen-2" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="card-surface p-10 text-center" data-testid="bookings-empty">
          <ClipboardList className="w-8 h-8 text-terracotta mx-auto mb-4" strokeWidth={1.5} />
          <p className="font-display text-2xl text-charcoal mb-2">No bookings yet</p>
          <p className="text-charcoal/60 mb-6">Head to the shop or use the AI Planner to arrange your first celebration.</p>
          <button onClick={() => navigate("/shop")} className="pill-btn pill-btn-primary" data-testid="bookings-shop">Browse shop</button>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((b, i) => (
            <motion.div key={b.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="card-surface p-6" data-testid={`booking-card-${i}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta">Booking · {b.id}</p>
                  <p className="font-display text-2xl text-charcoal mt-1">{b.date} · {b.time_slot}</p>
                  <p className="text-sm text-charcoal/60 mt-1">{b.address}</p>
                  {b.occasion && <p className="text-sm text-charcoal/60 mt-1">{b.occasion}</p>}
                </div>
                <div className="text-right">
                  <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold border ${STATUS_COLORS[b.status] || STATUS_COLORS.Pending}`}
                    data-testid={`booking-status-${i}`}>{b.status}</span>
                  {b.final_amount && b.final_amount !== b.total ? (
                    <div className="mt-3" data-testid={`booking-final-amount-${i}`}>
                      <p className="text-xs text-charcoal/50 line-through">Estimate ₹{b.total}</p>
                      <p className="font-display text-2xl text-charcoal">₹{b.final_amount}</p>
                      <p className="text-[11px] text-charcoal/50">Final amount</p>
                    </div>
                  ) : (
                    <p className="font-display text-2xl text-charcoal mt-3">₹{b.final_amount || b.total}</p>
                  )}
                  <PaymentBlock b={b} i={i} onPaid={load} whatsapp={whatsapp} />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-warm">
                <ul className="text-sm text-charcoal/80 space-y-1">
                  {b.items.map((it, idx) => (
                    <li key={idx} className="flex justify-between">
                      <span>{it.name} × {it.quantity}</span>
                      <span>₹{it.price * it.quantity}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {b.vendor_note && (
                <div className="mt-4 bg-sage/10 border border-sage/30 rounded-xl p-3 text-sm text-charcoal/80">
                  <b>From concierge:</b> {b.vendor_note}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
